import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { completeGoogleOAuth } from "../api/googleIntegration";

const GoogleOAuthCallback = () => {
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const errorParam = searchParams.get("error");

        if (errorParam) {
          throw new Error(searchParams.get("error_description") || "Google auth was cancelled or failed");
        }

        if (!code || !state) {
          throw new Error("No authorization code received from Google");
        }

        // Exchange code for tokens
        const result = await completeGoogleOAuth(code, state);

        if (!result.success) {
          throw new Error(result.error || "Failed to complete Google connection");
        }

        setAccount(result.data);
        setStatus("success");

        // Redirect after short delay
        setTimeout(() => {
          window.location.href = "/?tab=google-integrations";
        }, 2000);
      } catch (err) {
        console.error("[GoogleOAuthCallback] Error:", err);
        setError(err.message);
        setStatus("error");
      }
    };

    processCallback();
  }, []);

  return (
    <div className="min-h-screen bg-[#050810] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[#0d1424] border border-white/10 rounded-3xl overflow-hidden shadow-2xl p-8">
          {status === "processing" && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#ff4d4d] animate-spin" />
              <h2 className="text-2xl font-semibold mb-2">Connecting Google</h2>
              <p className="text-white/60">Please wait while we complete the connection...</p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-6 text-emerald-400" />
              <h2 className="text-2xl font-semibold mb-2">Google Connected!</h2>
              <p className="text-white/60 mb-4">
                {account?.google_email || "Your Google account"} is now connected.
              </p>
              <p className="text-sm text-white/40">Redirecting to settings...</p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 mx-auto mb-6 text-amber-400" />
              <h2 className="text-2xl font-semibold mb-2">Connection Failed</h2>
              <p className="text-white/60 mb-6">{error}</p>
              <div className="flex flex-col gap-3">
                <a
                  href="/?tab=google-integrations"
                  className="px-5 py-3 rounded-2xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white font-medium text-center transition-colors"
                >
                  Try Again
                </a>
                <a
                  href="/"
                  className="px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-medium text-center transition-colors"
                >
                  Back to Dashboard
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
